import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { APIStatus } from '@application/enums/api-status';
import { clearSelectedUserWithRoles, resetSearchString, setSearchString } from '@application/features/user/userSlice';
import { createUserAsync, getAllUsersPaginatedAsync, getUserWithRolesByIdAsync, toggleUserActiveAsync, updateUserAsync } from '@application/features/user/userThunk';
import { hasPermission } from '@application/helpers/permission-helper';
import { useAppDispatch } from '@application/store/useStore';
import { Box, Button, IconButton, Switch, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomAvatar from '@ui/components/common/Avatar';
import BDrawer from '@ui/components/common/BDrawer';
import CustomDialog from '@ui/components/common/Dialog';
import { PrimaryGrid } from '@ui/components/common/Grid';
import ResponsiveSearch from '@ui/components/common/ResponsiveSearch';
import { PageLayout } from '@ui/components/PageLayout';
import { centered, centeredRowStack, colCenter } from '@ui/style/style';
import { AddCircle, User } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AddEditUser from './AddEditUser';
import AddEditUserDialog from './AddEditUser/AddEditUserDialog';
import useUsersStyles from './useUsersStyles';
import { Iconly } from 'react-iconly';
const Users = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useUsersStyles();
    const dispatch = useAppDispatch();
    const [editUser, setEditUser] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
    const [switchTargetRow, setSwitchTargetRow] = useState(null);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [openAddEditUserDialog, setOpenAddEditUserDialog] = useState(false);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const selectedUser = useSelector((state) => state.auth.currentUser);
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    const { users, status, selectedUserWithRoles, searchString } = useSelector((state) => state.user);
    const canReadUser = hasPermission(selectedUser?.permissions, 'Permissions.Users.Read');
    const canCreateUser = hasPermission(selectedUser?.permissions, 'Permissions.Users.Create');
    const canUpdateUser = hasPermission(selectedUser?.permissions, 'Permissions.Users.Update');
    const canDeleteUser = hasPermission(selectedUser?.permissions, 'Permissions.Users.Delete');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const rows = users?.items.map((user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.roleName,
        company: user.companyName,
        contact: `${user.email}\n${user.phoneNo || ''}`,
        active: user.isActive,
        userProfilePic: user.profilePictureUrl
    })) || [];
    const isLoggedUserIsAdmin = selectedUser &&
        Array.isArray(selectedUser?.role) &&
        selectedUser.role.includes('HappinessManager');
    const userColumns = [
        {
            field: 'userProfilePic',
            headerName: '',
            minWidth: 50,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                const src = params.value;
                const firstName = params.row.firstName;
                const lastName = params.row.lastName;
                const fullName = `${firstName || ''} ${lastName || ''}`.trim();
                return (_jsx(Box, { width: '100%', height: '100%', onClick: (event) => event.stopPropagation(), sx: colCenter, children: _jsx(CustomAvatar, { src: src, name: fullName }) }));
            }
        },
        {
            field: 'firstName',
            headerName: t('labels.firstName'),
            flex: 1,
            minWidth: 130,
            type: 'string',
            sortable: false
        },
        {
            field: 'lastName',
            headerName: t('labels.lastName'),
            flex: 1,
            minWidth: 130,
            sortable: false,
            type: 'string'
        },
        {
            field: 'role',
            sortable: false,
            headerName: t('labels.role'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                let roles = params.value;
                if (!roles || !Array.isArray(roles))
                    return _jsx("span", { children: "-" });
                const distinctRoles = Array.from(new Set(roles));
                const displayText = distinctRoles.length > 1 ? `${distinctRoles[0]}...` : distinctRoles[0];
                if (distinctRoles.length > 1) {
                    return (_jsx(Tooltip, { placement: 'bottom', title: _jsx(Box, { sx: { maxWidth: 300, maxHeight: 150, overflow: 'auto' }, children: distinctRoles.join(', ') }), arrow: true, children: _jsx("span", { children: displayText }) }));
                }
                else {
                    return _jsx("span", { children: displayText });
                }
            }
        },
        ...(isLoggedUserIsAdmin
            ? [
                {
                    field: 'company',
                    sortable: false,
                    headerName: t('labels.company'),
                    flex: 1,
                    minWidth: 150
                }
            ]
            : []),
        {
            field: 'contact',
            headerName: t('labels.contact'),
            flex: 1,
            align: 'left',
            minWidth: 250,
            sortable: false,
            renderCell: (params) => {
                const [email, phoneNo] = (params.value || '').split('\n');
                return (_jsxs(Box, { sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        height: '100%'
                    }, children: [_jsx(Typography, { variant: 'body2', children: email }), phoneNo && (_jsx(Typography, { variant: 'body2', sx: { color: theme.palette.text.secondary }, children: `+${phoneNo}` }))] }));
            }
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 120,
            sortable: false,
            renderCell: (params) => (_jsx(Box, { onClick: (event) => event.stopPropagation(), children: _jsx(Switch, { size: 'medium', checked: params.row.active, disabled: params.row.id === selectedUser?.id || !canUpdateUser, onChange: () => {
                        setSwitchTargetRow(params.row);
                        setSwitchDialogOpen(true);
                    } }) }))
        },
        {
            field: 'actions',
            headerName: t('labels.actions'),
            minWidth: 80,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (_jsx(Box, { sx: { ...centered, alignItems: 'center', height: '100%' }, gap: 1, onClick: (event) => event.stopPropagation(), children: _jsx(IconButton, { size: 'small', onClick: () => {
                        handleEditClick(params.row.id);
                    }, disabled: !canUpdateUser, children: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }) }) }))
        }
    ];
    useEffect(() => {
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchString
        };
        dispatch(getAllUsersPaginatedAsync(request));
        // return () => {
        //   dispatch(resetSearchString());
        // };
    }, [dispatch, paginationModel.page, paginationModel.pageSize, selectedCompanyId, searchString]);
    useEffect(() => {
        if (editUser && selectedUserId) {
            dispatch(clearSelectedUserWithRoles());
            dispatch(getUserWithRolesByIdAsync(selectedUserId));
        }
    }, [dispatch, editUser, selectedUserId]);
    const handleToggleRoleActive = async (data) => {
        let payload = {
            id: data.id,
            isActive: !data.active
        };
        await dispatch(toggleUserActiveAsync(payload))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message || t('users.userUpdatedSuccessfully'));
                handleActionAfterSubmission();
            }
            else {
                toast.error(response.message || t('users.failedToUpdateUser'));
            }
        })
            .catch((error) => {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || t('users.failedToUpdateUser'));
        });
    };
    const handleActionAfterSubmission = () => {
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        };
        dispatch(getAllUsersPaginatedAsync(request));
        if (isSmallScreen) {
            setDrawerOpen(false);
        }
        else {
            setOpenAddEditUserDialog(false);
        }
        setEditUser(false);
    };
    const handleAddUser = async (data) => {
        try {
            await dispatch(createUserAsync(data))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || t('users.userCreatedSuccessfully'));
                    handleActionAfterSubmission();
                }
                else {
                    toast.error(response.message || t('users.failedToCreateUser'));
                }
            });
        }
        catch (error) {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || t('users.failedToCreateUser'));
        }
    };
    const handleEditUser = async (data) => {
        try {
            await dispatch(updateUserAsync(data))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || t('users.userUpdatedSuccessfully'));
                    handleActionAfterSubmission();
                }
                else {
                    toast.error(response.message || t('users.failedToUpdateUser'));
                }
            });
        }
        catch (error) {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || t('users.failedToUpdateUser'));
        }
    };
    const handleUserSubmit = (data) => {
        if (editUser) {
            handleEditUser(data);
        }
        else {
            handleAddUser(data);
        }
    };
    const handleEditClick = (userId) => {
        setSelectedUserId(userId);
        setEditUser(true);
        if (isSmallScreen) {
            setDrawerOpen(true);
        }
        else {
            setOpenAddEditUserDialog(true);
        }
    };
    return (_jsxs(PageLayout, { title: t('common.users'), showLeftIcon: true, leftIcon: _jsx(User, { variant: 'Outline' }), secondTitle: t('users.description'), rightContent: _jsx(Button, { disabled: !canCreateUser, startIcon: _jsx(AddCircle, {}), variant: 'contained', fullWidth: isSmallScreen, size: isSmallScreen ? 'medium' : 'large', color: 'primary', onClick: () => {
                if (isSmallScreen) {
                    setEditUser(false);
                    setSelectedUserId(null);
                    setDrawerOpen(true);
                }
                else {
                    setOpenAddEditUserDialog(true);
                }
            }, children: t('users.addUser') }), children: [isSmallScreen ? (_jsx(_Fragment, { children: _jsx(BDrawer, { anchor: 'bottom', title: editUser ? t('users.editUser') : t('users.addUser'), open: drawerOpen, onClose: () => {
                        setEditUser(false);
                        setDrawerOpen(false);
                        setSelectedUserId(null);
                    }, children: _jsx(AddEditUser, { edit: editUser, user: editUser ? selectedUserWithRoles : null, onSubmit: handleUserSubmit, onCancel: () => {
                            setDrawerOpen(false);
                            setEditUser(false);
                            setSelectedUserId(null);
                        } }) }) })) : (_jsx(Box, { children: openAddEditUserDialog && (_jsx(AddEditUserDialog, { open: openAddEditUserDialog, edit: editUser, user: editUser ? selectedUserWithRoles : null, onClose: () => {
                        setOpenAddEditUserDialog(false);
                        setEditUser(false);
                        setSelectedUserId(null);
                    }, onSubmitUser: handleUserSubmit })) })), canReadUser ? (_jsxs(Box, { sx: { ...styles.userContainer }, children: [_jsx(Box, { display: 'flex', justifyContent: 'flex-end', children: _jsx(ResponsiveSearch, { value: searchString || '', onChange: (value) => dispatch(setSearchString(value)), placeholder: t('users.searchUsers'), onClear: () => dispatch(resetSearchString()) }) }), _jsx(PrimaryGrid, { rows: rows, haveSearch: true, showFilters: false, 
                        //checkboxSelection
                        columns: userColumns, maxHeight: 'calc(100vh - 180px)', paginationMode: 'server', noRowsText: t('dataGrid.noRowsAdd', {
                            entityPlural: t('common.users').toLowerCase?.(),
                            entitySingular: t('users.user').toLowerCase?.()
                        }), onSearchChange: (value) => {
                            dispatch(setSearchString(value));
                        }, paginationModel: paginationModel, rowCount: users?.totalCount || 0, loading: status === APIStatus.PENDING, onPaginationModelChange: setPaginationModel, disableRowSelectionOnClick: true, showHeaderControls: false })] })) : (_jsx(Typography, { color: 'error', sx: { mt: 4 }, children: t('common.noPermissions') })), openDeleteConfirmation &&
                (isSmallScreen ? (_jsx(BDrawer, { open: openDeleteConfirmation, onClose: () => setOpenDeleteConfirmation(false), title: t('common.deleteConfirmation'), dialogActions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setOpenDeleteConfirmation(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: () => {
                                    setOpenDeleteConfirmation(false);
                                }, sx: {
                                    backgroundColor: theme.palette.error.main
                                }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) })) : (_jsx(CustomDialog, { open: openDeleteConfirmation, title: t('common.deleteConfirmation'), onClose: () => setOpenDeleteConfirmation(false), backgroundVariant: 'white', actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', onClick: () => setOpenDeleteConfirmation(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: () => {
                                        setOpenDeleteConfirmation(false);
                                    }, sx: {
                                        backgroundColor: theme.palette.error.main
                                    }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) }))), switchDialogOpen &&
                switchTargetRow &&
                (isSmallScreen ? (_jsx(BDrawer, { open: switchDialogOpen, onClose: () => setSwitchDialogOpen(false), title: switchTargetRow.active
                        ? t('users.deactivateUser')
                        : t('users.activateUser'), dialogActions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setSwitchDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: switchTargetRow.active ? 'error' : 'inherit', fullWidth: true, onClick: () => {
                                    handleToggleRoleActive(switchTargetRow);
                                    setSwitchDialogOpen(false);
                                }, sx: {
                                    backgroundColor: switchTargetRow.active ? theme.palette.error.main : undefined
                                }, children: switchTargetRow.active
                                    ? t('actions.deactivate')
                                    : t('actions.activate') })] }), children: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: switchTargetRow.active
                                ? t('users.userDeactivationConfirm')
                                : t('users.userActivationConfirm') }) }) })) : (_jsx(CustomDialog, { open: switchDialogOpen, title: switchTargetRow.active
                        ? t('users.deactivateUser')
                        : t('users.activateUser'), onClose: () => setSwitchDialogOpen(false), actions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setSwitchDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: switchTargetRow.active ? 'error' : 'inherit', fullWidth: true, onClick: () => {
                                    handleToggleRoleActive(switchTargetRow);
                                    setSwitchDialogOpen(false);
                                }, sx: {
                                    backgroundColor: switchTargetRow.active ? theme.palette.error.main : undefined
                                }, children: switchTargetRow.active
                                    ? t('actions.deactivate')
                                    : t('actions.activate') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: switchTargetRow.active
                                ? t('users.userDeactivationConfirm')
                                : t('users.userActivationConfirm') }) }) })))] }));
};
export default Users;
